import React, { useState, useEffect, useRef } from 'react'
import GuestLinkGenerator from '../components/GuestLinkGenerator.jsx'
import PrintableInvite from '../components/PrintableInvite.jsx'
import GalleryFeature from '../components/GalleryFeature.jsx'
import ClientSeatChecker from './ClientSeatChecker.jsx'
import GuestGreeting from '../components/GuestGreeting.jsx'
import '../styles/ClientDashboard.css'
import '../styles/ClientDashboardResponsiveTabs.css'
import '../styles/ClientDashboardFeatureAvailability.css'
import '../styles/ClientDashboardSeatCheckerCompact.css'

const CODE_STORAGE_KEY = 'seat_checker_access_code'
const ADMIN_CHECKER_DEEPLINK_KEY = 'seat_checker_admin_deeplink'

function syncAccessCodeUrl(code) {
  if (typeof window === 'undefined') return

  const normalizedCode = String(code || '').trim().toUpperCase()
  const nextUrl = normalizedCode
    ? `${window.location.pathname}?code=${encodeURIComponent(normalizedCode)}`
    : window.location.pathname

  window.history.replaceState({}, '', nextUrl)
}

function consumeAdminDeepLink(code) {
  const normalizedCode = String(code || '').trim().toUpperCase()
  const raw = localStorage.getItem(ADMIN_CHECKER_DEEPLINK_KEY) || ''

  if (!raw || !normalizedCode) return false

  try {
    const parsed = JSON.parse(raw)
    const savedCode = String(parsed?.code || '').trim().toUpperCase()
    const expiresAt = Number(parsed?.expiresAt || 0)
    const isValid = savedCode === normalizedCode && Number.isFinite(expiresAt) && expiresAt > Date.now()
    localStorage.removeItem(ADMIN_CHECKER_DEEPLINK_KEY)
    return isValid
  } catch {
    localStorage.removeItem(ADMIN_CHECKER_DEEPLINK_KEY)
    return false
  }
}

const TABS = [
  { id: 'seat-checker', label: 'Seat Checker', short: 'Seat' },
  { id: 'guest-links', label: 'Guest Links', short: 'Links' },
  { id: 'printable', label: 'Printable', short: 'Printable' },
  { id: 'gallery', label: 'Gallery', short: 'Gallery' },
]

export default function ClientDashboard() {
  const [active, setActive] = useState('seat-checker')
  const [tabsCollapsed, setTabsCollapsed] = useState(false)
  const [tabsMenuOpen, setTabsMenuOpen] = useState(false)
  const tabsRef = useRef(null)
  const tabsMeasureRef = useRef(null)

  const [accessCode, setAccessCode] = useState('')
  const [accessInput, setAccessInput] = useState('')
  const [defaultClientId, setDefaultClientId] = useState('')
  const [defaultPath, setDefaultPath] = useState('/')

  useEffect(() => {
    const urlCode = String(new URLSearchParams(window.location.search).get('code') || '').trim().toUpperCase()

    if (urlCode && consumeAdminDeepLink(urlCode)) {
      localStorage.setItem(CODE_STORAGE_KEY, urlCode)
      setAccessCode(urlCode)
      setAccessInput(urlCode)
    } else {
      if (urlCode) syncAccessCodeUrl('')
      const saved = localStorage.getItem(CODE_STORAGE_KEY) || ''
      setAccessCode(saved)
      setAccessInput(saved)
    }

    const dClient = localStorage.getItem('client_default_id') || ''
    const dPath = localStorage.getItem('client_default_path') || '/'
    setDefaultClientId(dClient)
    setDefaultPath(dPath)
  }, [])

  useEffect(() => {
    function measureTabs() {
      const measureList = tabsMeasureRef.current
      if (!measureList) return

      const requiredWidth = measureList.scrollWidth + 20
      const availableWidth = Math.max(0, window.innerWidth - 36)
      const shouldCollapse = requiredWidth > availableWidth

      setTabsCollapsed(shouldCollapse)
      if (!shouldCollapse) setTabsMenuOpen(false)
    }

    const animationFrame = window.requestAnimationFrame(measureTabs)
    window.addEventListener('resize', measureTabs)

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(measureTabs)
      : null

    if (tabsMeasureRef.current && resizeObserver) {
      resizeObserver.observe(tabsMeasureRef.current)
    }

    if (document.fonts?.ready) {
      document.fonts.ready.then(measureTabs)
    }

    return () => {
      window.cancelAnimationFrame(animationFrame)
      window.removeEventListener('resize', measureTabs)
      resizeObserver?.disconnect()
    }
  }, [accessCode])

  useEffect(() => {
    if (!tabsMenuOpen) return undefined

    function handlePointerDown(event) {
      if (tabsRef.current && !tabsRef.current.contains(event.target)) {
        setTabsMenuOpen(false)
      }
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setTabsMenuOpen(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [tabsMenuOpen])

  function applyAccessCode() {
    const normalized = (accessInput || '').trim().toUpperCase()
    if (!normalized) return
    localStorage.setItem('seat_checker_access_code', normalized)
    setAccessCode(normalized)
  }

  function clearAccessCode() {
    localStorage.removeItem('seat_checker_access_code')
    setAccessCode('')
    setAccessInput('')
  }

  function logout() {
    clearAccessCode()
    window.location.href = '/'
  }

  const activeTab = TABS.find((tab) => tab.id === active) || TABS[0]

  if (!accessCode) {
    return (
      <div className="client-dashboard container">
        <div className="access-gate">
          <h2>Client Dashboard Access</h2>
          <p className="subtitle">Enter the seat-checker access code provided by the event organizer to continue.</p>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <input className="field-input" value={accessInput} onChange={e => setAccessInput(e.target.value.toUpperCase())} placeholder="Access code" />
            <button className="btn primary" onClick={applyAccessCode}>Apply</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="client-dashboard container">
      <button
        type="button"
        className="btn dashboard-mobile-logout-button"
        onClick={logout}
        aria-label="Logout from Client Dashboard"
      >
        Logout
      </button>

      <header className="dashboard-header">
        <GuestGreeting autoDetect className="dashboard-guest-greeting" />
        <div className="dashboard-heading-copy">
          <h1 className="title">Client Dashboard</h1>
          <p className="subtitle">Simple tools for generating guest links, printing invites, and managing seating.</p>
        </div>
        <div className="header-actions">
          <button type="button" className="btn dashboard-logout-button" onClick={logout}>
            Logout
          </button>
        </div>
      </header>

      <nav
        ref={tabsRef}
        className={`tabs ${tabsCollapsed ? 'tabs-collapsed' : ''} ${tabsMenuOpen ? 'menu-open' : ''}`}
        aria-label="Client dashboard sections"
      >
        <button
          type="button"
          className="mobile-toggle"
          aria-expanded={tabsMenuOpen}
          aria-controls="tabs-list"
          onClick={() => setTabsMenuOpen((open) => !open)}
        >
          <span className="mobile-toggle-label">{activeTab.label}</span>
          <span className="mobile-toggle-chevron" aria-hidden="true">⌃</span>
        </button>

        <ul
          id="tabs-list"
          className="tabs-list"
          role="tablist"
          aria-hidden={tabsCollapsed && !tabsMenuOpen}
        >
          {TABS.map(t => (
            <li key={t.id} role="presentation">
              <button
                type="button"
                role="tab"
                aria-selected={active === t.id}
                tabIndex={tabsCollapsed && !tabsMenuOpen ? -1 : 0}
                className={`tab ${active === t.id ? 'active' : ''}`}
                onClick={() => {
                  setActive(t.id)
                  setTabsMenuOpen(false)
                }}
              >
                <span className="tab-label-full">{t.label}</span>
                <span className="tab-label-short">{t.short}</span>
              </button>
            </li>
          ))}
        </ul>
      </nav>

      <div ref={tabsMeasureRef} className="tabs-measure-list" aria-hidden="true">
        {TABS.map((tab) => (
          <span key={tab.id} className="tab tabs-measure-item">
            <span className="tab-label-full">{tab.label}</span>
            <span className="tab-label-short">{tab.short}</span>
          </span>
        ))}
      </div>

      <main className="tab-panels">
        <section className={`tab-panel ${active === 'guest-links' ? 'visible' : ''}`}>
          <div className="card-body">
            <GuestLinkGenerator
              defaultClientId={defaultClientId}
              defaultPath={defaultPath}
              purpose="Create a customized invitation link for a specific guest, including their name, role, and a personalized message when this feature is enabled."
              onUnavailableClose={() => setActive('seat-checker')}
            />
          </div>
        </section>

        <section className={`tab-panel ${active === 'printable' ? 'visible' : ''}`}>
          <div className="card-body preview">
            <PrintableInvite
              purpose="Get a print-ready copy of your invitation website and print its beautiful layout using your preferred paper size when this feature is enabled."
              onUnavailableClose={() => setActive('seat-checker')}
            />
          </div>
        </section>

        <section className={`tab-panel ${active === 'seat-checker' ? 'visible' : ''}`}>
          <div className="card-body">
            <ClientSeatChecker accessCode={accessCode} />
          </div>
        </section>

        <section className={`tab-panel ${active === 'gallery' ? 'visible' : ''}`}>
          <div className="card-body">
            <GalleryFeature
              accessCode={accessCode}
              purpose="View, manage, download, and control the privacy of your event photos when the Gallery feature is enabled for your invitation."
              onUnavailableClose={() => setActive('seat-checker')}
            />
          </div>
        </section>
      </main>
    </div>
  )
}
